import type { Lieferwunsch } from './detail';
// Domain-Typen fuer die Bestellerfassung (SPEC-004).
// Feldnamen matchen die Backend-Response von api/bestellungen/create.php
// und die Lookup-Endpoints.

export type Bestellweg = {
  id: number;
  code: string;
  label: string;
  icon: string | null;
  sort_order: number;
};

export type Verlag = {
  id: number;
  code: string;
  label: string;
  lieferzeit_tage_default: number | null;
};

export type KundeSuchTreffer = {
  id: number;
  anzeigename: string;
  ort: string | null;
  bonus_kundennr: string | null;
  has_email: boolean;
};

/**
 * Ein Beschaffungsweg, der schon beim Erfassen feststand (A4).
 *
 * Ohne `meldung`/`meldung_am`: Was der Lieferant meldet, kann es vor der
 * Bestellung noch nicht geben. Die Felder stehen am Journaleintrag und
 * werden dort nachgetragen — hier wären sie eine Behauptung über einen
 * Vorgang, den es noch nicht gab.
 */
export type BeschaffungInput = {
  beschaffungsweg_id: number;
  menge: number;
  /** Kalendertag "YYYY-MM-DD", verbatim — nicht UTC-gerechnet. */
  bestellt_am: string;
  verlag_text: string | null;
  kanal: string | null;
};

// Frontend-interne Position-Struktur. `_uid` ist client-only fuer
// React-Keys und Reducer-Updates, wird vor dem Submit gestrippt.
export type PositionInput = {
  _uid: string;
  isbn: string | null;
  titel: string;
  autor: string | null;
  verlag_text: string | null;
  verlag_id: number | null;
  preis_cent: number | null;
  menge: number;
  cover_url: string | null;
  ist_freitext: boolean;
  notiz: string | null;
  /**
   * „Sagt mir Bescheid, wenn DIESER Titel da ist." (A56, #135)
   *
   * Ergänzt den Wunsch an der Bestellung, ersetzt ihn nicht. Vorgabe
   * aus — es kommt nicht oft vor.
   */
  nachricht_wunsch: boolean;
  /** Wege, die beim Erfassen schon feststanden (A4). Leer ist der Normalfall. */
  beschaffungen: BeschaffungInput[];
};

/** Eine leere Position, damit die Pflichtfelder an einer Stelle stehen. */
export function leerePosition(uid: string): PositionInput {
  return {
    _uid: uid,
    isbn: null,
    titel: '',
    autor: null,
    verlag_text: null,
    verlag_id: null,
    preis_cent: null,
    menge: 1,
    cover_url: null,
    ist_freitext: true,
    notiz: null,
    nachricht_wunsch: false,
    beschaffungen: []
  };
}

export type NeuerKundeInput = {
  anrede: string;
  vorname: string;
  nachname: string;
  firma: string;
  email: string;
  telefon: string;
  bonus_kundennr: string;
  adresse_strasse: string;
  adresse_plz: string;
  adresse_ort: string;
  adresse_land: string;
  notiz: string;
};

export function emptyNeuerKunde(): NeuerKundeInput {
  return {
    anrede: '',
    vorname: '',
    nachname: '',
    firma: '',
    email: '',
    telefon: '',
    bonus_kundennr: '',
    adresse_strasse: '',
    adresse_plz: '',
    adresse_ort: '',
    adresse_land: 'Deutschland',
    notiz: ''
  };
}

export type AdresseInput = {
  anrede: string;
  strasse: string;
  plz: string;
  ort: string;
  land: string;
};

export function emptyAdresse(): AdresseInput {
  return { anrede: '', strasse: '', plz: '', ort: '', land: 'Deutschland' };
}

// Wire-Format fuer POST /api/bestellungen/create.php.
// Leere Strings werden im Serializer zu null, damit die Backend-Validierung
// saubere Daten sieht (too_long_or_invalid waere sonst false positive fuer "").
export type CreateBestellungRequest = {
  bestellweg_id: number;
  /**
   * Abholung oder Versand, als ID der Versandart (E20, A7.2).
   *
   * Wird im SELBEN Aufruf geschrieben. Vorher setzte die Oberflaeche sie
   * in einem zweiten Aufruf an versandart.php — scheiterte der, stand die
   * Bestellung ohne Versandart da, und ihre Sendung liess sich nicht mehr
   * buchen.
   *
   * `null` ist erlaubt: das Formular verlangt die Wahl, die Schnittstelle
   * nicht (sonst fiele die Uebungsbestellung der Tour mit heraus).
   */
  versandart_id: number | null;
  kunde_id: number | null;
  neuer_kunde: NeuerKundeWire | null;
  /**
   * Der Geschenkwunsch aus dem Gespraech (A25).
   *
   * Die Bestellung traegt den WUNSCH, die Sendung die AUSFUEHRUNG. Der
   * Wunsch kann bestehen, ohne dass je eine Sendung entsteht — bei
   * Versand fuehrt seit A11 ein Weg direkt in den Endzustand.
   */
  ist_geschenk: boolean;
  /**
   * Der Kundenwunsch aus dem Gespräch (A56, #135).
   *
   * Optional auf API-Ebene: Ein Aufruf, der die Frage gar nicht kennt —
   * Shopify-Import, Einführungstour, ältere Oberfläche — soll nicht
   * scheitern und bekommt die Vorgabe. Ein Aufruf mit einem
   * UNBEKANNTEN Lieferwunsch bekommt dagegen einen Fehler; still auf
   * die Vorgabe zurückzusetzen fiele erst auf, wenn ein Paket zu spät
   * rausgeht.
   */
  lieferwunsch?: Lieferwunsch;
  nachricht_wunsch?: boolean;
  rechnung_abweichend: boolean;
  rechnung_anrede: string | null;
  rechnung_strasse: string | null;
  rechnung_plz: string | null;
  rechnung_ort: string | null;
  rechnung_land: string | null;
  shopify_order_id: string | null;
  shopify_order_number: string | null;
  notiz: string | null;
  /** Kalenderdatum "YYYY-MM-DD"; null, wenn kein Termin vereinbart ist. */
  abholdatum_zugesagt: string | null;
  /** Uebungsbestellung der Einfuehrungstour (nur fuer den Erfasser sichtbar). */
  ist_uebung: boolean;
  positionen: PositionWire[];
};

export type PositionWire = Omit<PositionInput, '_uid'>;

export type NeuerKundeWire = {
  anrede: string | null;
  vorname: string | null;
  nachname: string | null;
  firma: string | null;
  email: string | null;
  telefon: string | null;
  bonus_kundennr: string | null;
  adresse_strasse: string | null;
  adresse_plz: string | null;
  adresse_ort: string | null;
  adresse_land: string | null;
  notiz: string | null;
};

export type CreateBestellungResponse = {
  bestellung: { id: number; bestellnr: string };
};

// VLB-Lookup (SPEC-005)
/**
 * Lieferbar, vergriffen — oder wissen wir es nicht? (#61)
 *
 * Drei Zustaende, nicht zwei. Was das VLB nicht eindeutig sagt, heisst
 * 'unbekannt' und wird auch so angezeigt: Schweigen liest sich am Tresen
 * wie „lieferbar".
 */
export type Lieferbarkeit = 'lieferbar' | 'nicht_lieferbar' | 'unbekannt';

export type VlbTitel = {
  isbn: string;
  vlb_product_id: string | null;
  titel: string | null;
  autor: string | null;
  verlag_text: string | null;
  preis_cent: number | null;
  erscheinungsjahr: number | null;
  cover_url: string | null;
  /** Fehlt bei Antworten, die vor A24 im Umlauf waren. */
  lieferbarkeit?: Lieferbarkeit;
  /** E-Book oder anderes Digitales (03.11.2026) — rechnet der Server. */
  digital?: boolean;
};

export type VlbLookupResponse = {
  cached: boolean;
  /**
   * Wann die Auskunft entstanden ist: bei einem Cache-Treffer das
   * Datum der Zeile, bei einem frischen Abruf jetzt. Verfuegbarkeit ist
   * verderblich und der Cache 30 Tage frisch — ohne das Datum waere ein
   * vier Wochen altes „lieferbar" von einem frischen nicht zu
   * unterscheiden.
   */
  stand_vom?: string;
  titel: VlbTitel;
};

// Katalog-Auflösung (K1/K3).
//
// Bewusst NICHT VlbTitel wiederverwendet (dieselbe Begründung wie bei
// der Freitextsuche darunter): der Katalogtreffer trägt Katalogname,
// Seite und einen Preis als TEXT vom Drucktermin — der darf angezeigt,
// aber nie in eine Position vorbelegt werden. Ein preis_cent-Feld, das
// hier immer fehlt, sähe an der Übernahme-Stelle aus wie ein Versehen.
export type KatalogTreffer = {
  /** Kanonische Form "120-042". */
  nummer: string;
  titel: string;
  autor: string | null;
  verlag: string | null;
  /** Nackte ISBN-13, oder null — 7 Titel des Herbstkatalogs haben keine. */
  isbn: string | null;
  katalogseite: string | null;
  /** Preisstand vom Drucktermin, nur zur Anzeige. */
  preis_text: string | null;
  /** "Herbst 2026" — aus dem Manifest des Abzugs. */
  katalog_label: string;
  /** Ist das der laufende Katalog? Sonst: "aus <Name>, Preis prüfen". */
  aktiv: boolean;
};

export type KatalogAufloesungResponse = {
  treffer: KatalogTreffer;
};

// VLB-Freitextsuche (#83, A23).
//
// Bewusst NICHT VlbTitel wiederverwendet: die Suchantwort traegt einen
// Untertitel und keinen Preis, der Lookup umgekehrt. Ein gemeinsamer Typ
// mit zwei optionalen Feldern verspraeche an jeder Stelle beides und
// haelte an keiner beides.
export type VlbSuchTreffer = {
  isbn: string | null;
  vlb_product_id: string | null;
  titel: string | null;
  untertitel: string | null;
  autor: string | null;
  verlag_text: string | null;
  erscheinungsjahr: number | null;
  produkttyp: string | null;
  /** E-Book oder anderes Digitales — rechnet der Server (vlbIstDigital). */
  digital?: boolean;
  // A23.3: Der Server liefert productAvailability, active und
  // wholesalers roh mit. Sie stehen hier bewusst NICHT im Typ — was im
  // Typ steht, wird frueher oder spaeter angezeigt, und angezeigt werden
  // sie erst, wenn der Wertebereich auf Prod gemessen ist (#61).
};

export type VlbSucheResponse = {
  treffer: VlbSuchTreffer[];
};
